import { Container, Graphics, Text, TextStyle } from 'pixi.js';

/**
 * A single comic-style speech bubble, anchored to whichever character is
 * "talking" — this is how Patch and Bit pose the case's questions to the
 * player, instead of a narrator reciting case text in the console below.
 *
 * One instance is reused for both characters (only one speaks at a time):
 * `type()` repositions it, recolors its outline to the speaker, and reveals
 * the line like a typewriter, then holds it fully visible for a reading
 * beat before resolving — so a line never gets swapped out the instant the
 * last letter lands. A tap on the bubble skips straight to that resolve, for
 * players who read faster or are replaying.
 */
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
// Comfortable reading pace — fast enough to feel alive, slow enough to read
// as it appears rather than racing to catch up once it's done.
const CHARS_PER_SECOND = 26;
// Extra time the finished line stays up before the sequence moves on,
// scaled by length so a short line doesn't linger and a long one doesn't flash by.
const MIN_HOLD_MS = 900;
const MAX_HOLD_MS = 3200;
const HOLD_MS_PER_CHAR = 32;

const PADDING_X = 12;
const PADDING_Y = 9;
const TAIL_HALF_WIDTH = 7;
const TAIL_HEIGHT = 11;
const EDGE_MARGIN = 8;

type Phase = 'typing' | 'holding' | 'idle';

export class SpeechBubble {
  readonly view = new Container();
  private readonly shape = new Graphics();
  private readonly label: Text;
  private readonly screenWidth: number;
  private readonly maxWidth: number;

  private fullText = '';
  private shown = 0;
  private phase: Phase = 'idle';
  private holdRemainingMs = 0;
  private anchorX = 0;
  private anchorY = 0;
  private color = 0xffffff;
  private resolveIdle: (() => void) | undefined;

  constructor(screenWidth: number, maxWidth: number) {
    this.screenWidth = screenWidth;
    this.maxWidth = maxWidth;
    this.view.addChild(this.shape);
    this.label = new Text({
      text: '',
      style: new TextStyle({
        fill: 0xeaf6ee,
        fontFamily: MONO,
        fontSize: 11.5,
        lineHeight: 15,
        wordWrap: true,
        wordWrapWidth: maxWidth - PADDING_X * 2,
      }),
    });
    this.view.addChild(this.label);
    this.view.visible = false;

    // Tap the bubble to skip the typing/reading pause — the player's choice
    // to move faster, never forced.
    this.shape.eventMode = 'static';
    this.shape.cursor = 'pointer';
    this.shape.on('pointertap', () => {
      this.skip();
    });
  }

  get busy(): boolean {
    return this.view.visible && this.phase !== 'idle';
  }

  /**
   * Shows the bubble with its tail pointing at (anchorX, anchorY) — the
   * character's head — types `text` into it, then holds it for a reading
   * beat. Resolves once that hold elapses, or immediately on `skip()`/`hide()`.
   */
  async type(text: string, anchorX: number, anchorY: number, color: number): Promise<void> {
    this.resolveIdle?.(); // a still-pending previous type() never got its resolve; release it
    this.fullText = text;
    this.shown = 0;
    this.phase = 'typing';
    this.anchorX = anchorX;
    this.anchorY = anchorY;
    this.color = color;
    this.label.text = '';
    this.view.visible = true;
    this.view.alpha = 1;
    this.redraw();
    return new Promise((resolve) => {
      this.resolveIdle = resolve;
    });
  }

  /** Jumps straight to the end: fully typed and past its reading hold. */
  skip(): void {
    if (this.phase === 'idle') return;
    this.shown = this.fullText.length;
    this.label.text = this.fullText;
    this.phase = 'idle';
    this.redraw();
    this.resolveIdle?.();
    this.resolveIdle = undefined;
  }

  hide(): void {
    this.view.visible = false;
    this.phase = 'idle';
    this.resolveIdle?.();
    this.resolveIdle = undefined;
  }

  private redraw(): void {
    const textWidth = this.fullText.length === 0 ? 0 : this.label.width;
    const bubbleWidth = Math.min(this.maxWidth, Math.max(40, textWidth) + PADDING_X * 2);
    const bubbleHeight = Math.max(this.label.height, 15) + PADDING_Y * 2;

    let left = this.anchorX - bubbleWidth / 2;
    left = Math.max(EDGE_MARGIN, Math.min(this.screenWidth - EDGE_MARGIN - bubbleWidth, left));
    const top = this.anchorY - bubbleHeight - TAIL_HEIGHT;
    const tailX = Math.max(
      left + TAIL_HALF_WIDTH * 2,
      Math.min(left + bubbleWidth - TAIL_HALF_WIDTH * 2, this.anchorX),
    );
    const bottom = top + bubbleHeight;

    this.shape
      .clear()
      .roundRect(left, top, bubbleWidth, bubbleHeight, 9)
      .fill({ color: 0x06130f, alpha: 0.95 })
      .stroke({ color: this.color, width: 1.6, alpha: 0.9 })
      .poly([
        tailX - TAIL_HALF_WIDTH,
        bottom,
        tailX + TAIL_HALF_WIDTH,
        bottom,
        this.anchorX,
        this.anchorY,
      ])
      .fill({ color: 0x06130f, alpha: 0.95 })
      .moveTo(tailX - TAIL_HALF_WIDTH, bottom)
      .lineTo(this.anchorX, this.anchorY)
      .lineTo(tailX + TAIL_HALF_WIDTH, bottom)
      .stroke({ color: this.color, width: 1.6, alpha: 0.9 });

    this.label.position.set(left + PADDING_X, top + PADDING_Y);
  }

  update(deltaMs: number): void {
    if (!this.view.visible || this.phase === 'idle') return;

    if (this.phase === 'typing') {
      const step = Math.max(1, Math.round((CHARS_PER_SECOND * deltaMs) / 1000));
      this.shown = Math.min(this.fullText.length, this.shown + step);
      this.label.text = this.fullText.slice(0, this.shown);
      this.redraw();
      if (this.shown >= this.fullText.length) {
        this.phase = 'holding';
        this.holdRemainingMs = Math.min(
          MAX_HOLD_MS,
          Math.max(MIN_HOLD_MS, this.fullText.length * HOLD_MS_PER_CHAR),
        );
      }
      return;
    }

    // phase === 'holding': the full line stays up, giving a beat to read it.
    this.holdRemainingMs -= deltaMs;
    if (this.holdRemainingMs <= 0) {
      this.phase = 'idle';
      this.resolveIdle?.();
      this.resolveIdle = undefined;
    }
  }

  destroy(): void {
    this.view.destroy({ children: true });
  }
}
